import type {
  TimedMetadataItem,
  UpdateTimedMetadataRequestDto,
} from '@mimir/common/dto/timedMetadataModels';
import type { TimedMdfUsageRule } from '@mimir/common/dto/timedMdfUsage';
import type { MetaDataSchema } from '@mimir/common/mdf/metadataSchema';
import type { ItemMetaData } from '@mimir/common/dto/fieldValue';
import type { Item } from '@mimir/common/dto/searchResultHit';
import type { OptionListDto } from '@mimir/common/dto/optionListDto';
import type { ItemMediaPlayerOps } from './itemMediaPlayerOps';
import type Timeline from '@mimir/client-common/timeline/Timeline';
import type { AxiosError } from 'axios';
import { type Ref, ref, computed } from 'vue';
import cloneDeep from 'lodash/cloneDeep';
import sortBy from 'lodash/sortBy';
import keyBy from 'lodash/keyBy';
import isEmpty from 'lodash/isEmpty';
import { createUuid } from '@mimir/common/util/createUuid';
import { getRestrictedFormUsageRules } from '@mimir/web/util/usageRestrictionsCheck';
import { getUsageRuleFromTimedMdf } from '@mimir/common/timed-mdf/usage-rules';
import { applyIngestTimeIfUsageRuleExists, getIngestTimeFromItem } from '@mimir/common/util/timedMetadata';
import { getTimedMetadata, updateTimedMetadata } from '@mimir/web/util/api';
import { logErrorAndShowToast } from '@mimir/web/util/uiError';
import { showToast, TOAST } from '@mimir/web/eventBus';
import { abortable, isAbortError } from '@mimir/common/util/abortControl';
import TimelineItem from '@mimir/web/model/timeline/TimelineItem';
import Vue from 'vue';

export interface TimedMetadataItemModel extends TimedMetadataItem {
  swimlane: number;
  hasValidationErrors: boolean;
  isNewNonSavedItem: boolean;
  usageRule?: TimedMdfUsageRule;
  timecode?: string;
  selected?: boolean;
}

export interface TimedMetadataModelCollection {
  items: TimedMetadataItemModel[];
  eventsSnapshot: TimedMetadataItemModel[];
}

export const ITEM_HEIGHT = 10;

interface MutableTimedMdfState {
  timedMdfSaving: Ref<boolean>;
  collectionItems: Ref<TimedMetadataItemModel[]>;
  eventsSnapshot: Ref<TimedMetadataItemModel[]>;
  selectedItem: Ref<TimedMetadataItemModel | null>;
  hoveredItem: Ref<TimedMetadataItemModel | null>;
  selectedTimedMdf: Ref<MetaDataSchema | null>;
  loadingCollection: Ref<boolean>;
  loadedCollection: Ref<boolean>;
  itemsWithChanges: Ref<string[]>;
  shouldAutoFocusSelectedItem: Ref<boolean>;
  timedMdfs: Ref<MetaDataSchema[]>;
}

export interface TimedMdfState {
  timedMdfSaving: Readonly<Ref<boolean>>;
  collectionItems: Readonly<Ref<TimedMetadataItemModel[]>>;
  eventsSnapshot: Readonly<Ref<TimedMetadataItemModel[]>>;
  selectedItem: Readonly<Ref<TimedMetadataItemModel | null>>;
  hoveredItem: Readonly<Ref<TimedMetadataItemModel | null>>;
  selectedTimedMdf: Readonly<Ref<MetaDataSchema | null>>;
  loadingCollection: Readonly<Ref<boolean>>;
  loadedCollection: Readonly<Ref<boolean>>;
  itemsWithChanges: Readonly<Ref<string[]>>;
  shouldAutoFocusSelectedItem: Readonly<Ref<boolean>>;
  timedMdfs: Readonly<Ref<MetaDataSchema[]>>;
}

const ensureReadOnly = (timedMdf: TimedMdf) => {
  // @ts-expect-error - enforce read-only state by throwing if state is directly mutated outside of ops
  timedMdf.state.selectedItem.value = null;
};
void ensureReadOnly;

export function timedMdfCollectionSorted(state: TimedMdfState): TimedMetadataItemModel[] {
  return sortBy(state.collectionItems.value, [item => item.startMs]);
}

export function timedMdfUsageRules(services: TimedMdfServices): TimedMdfUsageRule[] | undefined {
  const mdfs = services.getTimedMdfs();
  return getRestrictedFormUsageRules(mdfs);
}

export interface SetSelectedTimedMdfItemPayload {
  item: TimedMetadataItem | null;
  setStart: boolean;
  shouldAutoFocusSelectedItem: boolean;
  playerOps: ItemMediaPlayerOps;
}

export interface AddTimedMdfPayload {
  mdf: MetaDataSchema;
  timecode?: string;
  playerOps: ItemMediaPlayerOps;
}

export interface CopyTimedMdfPayload {
  oldTimedMdfItem: TimedMetadataItem;
  playerOps: ItemMediaPlayerOps;
}

export interface SaveTimedMdfItemsPayload {
  timedItemIds: string[];
  abortController: AbortController;
}

export interface TimedMdfOps {
  loadTimedMdf(): Promise<void>;
  cleanup(): void;

  setSelectedItem(payload: SetSelectedTimedMdfItemPayload): void;
  setHovered(item: TimedMetadataItemModel | null): void;
  setShouldAutoFocusSelectedItem(value: boolean): void;
  setSelectedItemHasErrors(hasErrors: boolean): void;

  add(info: AddTimedMdfPayload): void;
  addItem(item: TimedMetadataItemModel): void;
  addSelectedMdf(timecode?: string): Promise<void>;
  remove(item: TimedMetadataItemModel): void;
  copyTimedMdf(payload: CopyTimedMdfPayload): void;

  itemHasChanged(timedItemId: string, hasChanged: boolean): void;
  updateItemsWithSnapshot(): void;

  saveItems(payload: SaveTimedMdfItemsPayload): Promise<void>;

  setSelectedTimedMdf(mdf: MetaDataSchema | null): void;
  setLoadedCollection(loaded: boolean): void;
}

export interface TimedMdf {
  readonly state: TimedMdfState;
  ops: TimedMdfOps;
}

export interface TimedMdfServices {
  getTimedMdfs(): MetaDataSchema[];
  getOptionLists(): OptionListDto[];
  getCurrentUserId(): string;
  getCurrentItem(): Item | null;
  getTimeline(): Timeline;
  clearTimelineState(): void;
}

function removeTimedMdfFromState(
  collectionItems: TimedMetadataItemModel[],
  eventsSnapshot: TimedMetadataItemModel[],
  timedItemId: string,
): void {
  let itemRemovedFromCollection = false;

  for (let i = 0; i < collectionItems.length; i++) {
    if (collectionItems[i].id === timedItemId) {
      collectionItems.splice(i, 1);
      itemRemovedFromCollection = true;
    }

    if (eventsSnapshot[i] && eventsSnapshot[i].id === timedItemId) {
      eventsSnapshot.splice(i, 1);
    }

    if (itemRemovedFromCollection) {
      break;
    }
  }
}

function updateItemChanged(itemsWithChanges: string[], timedItemId: string, hasChanged: boolean): string[] {
  if (!hasChanged) {
    return itemsWithChanges.filter(id => id !== timedItemId);
  }
  const alreadyIn = itemsWithChanges.includes(timedItemId);
  if (!alreadyIn) {
    return [...itemsWithChanges, timedItemId];
  }
  return itemsWithChanges;
}

function modelToDto(model: TimedMetadataItemModel): TimedMetadataItem {
  return {
    id: model.id,
    data: cloneDeep(model.data),
    startMs: model.startMs,
    endMs: model.endMs,
  };
}

export function dtoToModel(dto: TimedMetadataItem, usageRules?: TimedMdfUsageRule[], timecode?: string): TimedMetadataItemModel {
  return {
    hasValidationErrors: false,
    isNewNonSavedItem: false,
    swimlane: 0,
    timecode,
    ...cloneDeep(dto),
    ...(usageRules && { usageRule: getUsageRuleFromTimedMdf(dto, usageRules) }),
  };
}

export function createTimedMdfStates(services: TimedMdfServices): TimedMdf {
  const mutableState: MutableTimedMdfState = {
    timedMdfSaving: ref(false),
    collectionItems: ref<TimedMetadataItemModel[]>([]),
    eventsSnapshot: ref<TimedMetadataItemModel[]>([]),
    selectedItem: ref<TimedMetadataItemModel | null>(null),
    hoveredItem: ref<TimedMetadataItemModel | null>(null),
    selectedTimedMdf: ref<MetaDataSchema | null>(null),
    loadingCollection: ref(false),
    loadedCollection: ref(false),
    itemsWithChanges: ref<string[]>([]),
    shouldAutoFocusSelectedItem: ref(true),
    timedMdfs: computed(() => services.getTimedMdfs()),
  };

  function setItemHasChanged(timedItemId: string, hasChanged: boolean): void {
    mutableState.itemsWithChanges.value = updateItemChanged(
      mutableState.itemsWithChanges.value,
      timedItemId,
      hasChanged,
    );
  }

  function setSelectedItemInternal(payload: SetSelectedTimedMdfItemPayload): void {
    mutableState.selectedItem.value = payload.item as TimedMetadataItemModel | null;
    mutableState.shouldAutoFocusSelectedItem.value = payload.shouldAutoFocusSelectedItem;
    if (payload.item !== null && payload.setStart) {
      payload.playerOps.seekToVirtualClipMediaPosition({ approximate: payload.item.startMs / 1000.0 });
    }
  }

  const ops: TimedMdfOps = {
    async loadTimedMdf(): Promise<void> {
      const currentItem = services.getCurrentItem();
      if (currentItem === null) return;
      if (mutableState.loadedCollection.value) return;

      const itemId = currentItem.id;
      mutableState.loadingCollection.value = true;
      try {
        const collection = await getTimedMetadata(itemId);
        if (services.getCurrentItem()?.id !== itemId) return;
        const mdfs = services.getTimedMdfs();
        const usageRules = getRestrictedFormUsageRules(mdfs);
        const items = collection.items.map(it => dtoToModel(it, usageRules, currentItem.timecode));
        mutableState.collectionItems.value = items;
        mutableState.eventsSnapshot.value = cloneDeep(items);
        mutableState.selectedItem.value = null;
        if (mdfs.length > 0) {
          mutableState.selectedTimedMdf.value = mdfs[0];
        }
        mutableState.loadedCollection.value = true;
      } catch (err) {
        if (services.getCurrentItem()?.id !== itemId) return;
        logErrorAndShowToast('Failed loading log events, please try again later', err, 'ex in loadTimedMdf');
      } finally {
        mutableState.loadingCollection.value = false;
      }
    },

    cleanup(): void {
      mutableState.selectedItem.value = null;
      mutableState.collectionItems.value = [];
      mutableState.eventsSnapshot.value = [];
      mutableState.timedMdfSaving.value = false;
      mutableState.itemsWithChanges.value = [];
      mutableState.loadingCollection.value = false;
      mutableState.loadedCollection.value = false;
    },

    setSelectedItem(payload: SetSelectedTimedMdfItemPayload): void {
      setSelectedItemInternal(payload);
    },

    setHovered(item: TimedMetadataItemModel | null): void {
      mutableState.hoveredItem.value = item;
    },

    setShouldAutoFocusSelectedItem(value: boolean): void {
      mutableState.shouldAutoFocusSelectedItem.value = value;
    },

    setSelectedItemHasErrors(hasErrors: boolean): void {
      if (mutableState.selectedItem.value) {
        mutableState.selectedItem.value.hasValidationErrors = hasErrors;
      }
    },

    add(info: AddTimedMdfPayload): void {
      const timeline = services.getTimeline();
      const virtualClipDurationInMs =
        info.playerOps.getVirtualClipDurationObs().currentValue().approximate * 1000.0;

      let timeRange = timeline.getTimeRange(
        virtualClipDurationInMs,
        info.playerOps.getVirtualClipStartObs().currentValue(),
      );
      let setStart = true;

      console.log(`timeRange = ${JSON.stringify(timeRange)}`);

      if (timeRange === null) {
        const currentTime = info.playerOps.getVirtualClipMediaPositionObs().currentValue().approximate * 1000.0;
        const logEventDuration = (info.mdf.duration ?? 5) * 1000;

        let start = currentTime;
        let end = Math.min(currentTime + logEventDuration, virtualClipDurationInMs);
        const minLogEventDuration = 1000;

        if ((end - minLogEventDuration) <= currentTime) {
          start = Math.max(0, virtualClipDurationInMs - minLogEventDuration);
          end = virtualClipDurationInMs;
        }

        timeRange = { start, end };
        setStart = false;
      } else {
        const end = Math.max(timeRange.start + TimelineItem.MIN_DURATION, timeRange.end);
        timeRange = {
          start: timeRange.start,
          end,
        };
      }

      const payload: ItemMetaData = {
        formId: info.mdf.id,
        formData: {},
      };
      const errors = info.mdf.sanitize(services.getOptionLists(), payload, false);
      const newTimedMdfItem: TimedMetadataItemModel = {
        id: createUuid(),
        swimlane: 0,
        hasValidationErrors: !isEmpty(errors),
        isNewNonSavedItem: true,
        startMs: timeRange.start,
        endMs: timeRange.end,
        data: payload,
        timecode: info.timecode,
        selected: true,
        creationInfo: {
          source: 'api',
          date: new Date().toISOString(),
          userId: services.getCurrentUserId(),
        },
      };
      mutableState.collectionItems.value.push(newTimedMdfItem);
      if (newTimedMdfItem.isNewNonSavedItem) {
        mutableState.eventsSnapshot.value.push(newTimedMdfItem);
      }
      setItemHasChanged(newTimedMdfItem.id, true);
      services.clearTimelineState();
      setSelectedItemInternal({
        playerOps: info.playerOps,
        item: newTimedMdfItem,
        setStart,
        shouldAutoFocusSelectedItem: true,
      });
    },

    addItem(item: TimedMetadataItemModel): void {
      mutableState.collectionItems.value.push(item);
      if (item.isNewNonSavedItem) {
        mutableState.eventsSnapshot.value.push(item);
      }
      setItemHasChanged(item.id, true);
    },

    async addSelectedMdf(timecode?: string): Promise<void> {
      if (!mutableState.loadedCollection.value) {
        await ops.loadTimedMdf();
      }
      if (mutableState.selectedTimedMdf.value !== null && timecode !== undefined) {
        // Note: addSelectedMdf in the old store dispatches 'add' with { mdf, timecode }
        // but that action needs playerOps which isn't available here. This method
        // preserves the old interface; callers that used addSelectedMdf should migrate
        // to calling ops.add() directly with the playerOps parameter.
        console.warn('addSelectedMdf called without playerOps — callers should use ops.add() directly');
      }
    },

    remove(timedItem: TimedMetadataItemModel): void {
      removeTimedMdfFromState(
        mutableState.collectionItems.value,
        mutableState.eventsSnapshot.value,
        timedItem.id,
      );
      const requiresSave = !timedItem.isNewNonSavedItem;
      setItemHasChanged(timedItem.id, requiresSave);
      if (mutableState.selectedItem.value?.id === timedItem.id) {
        mutableState.selectedItem.value = null;
      }
    },

    copyTimedMdf(payload: CopyTimedMdfPayload): void {
      const { oldTimedMdfItem, playerOps } = payload;
      const currentTime = playerOps.getVirtualClipMediaPositionObs().currentValue().approximate * 1000.0;
      const virtualClipDurationInMs = playerOps.getVirtualClipDurationObs().currentValue().approximate * 1000.0;
      let endMs = (oldTimedMdfItem.endMs - oldTimedMdfItem.startMs) + currentTime;
      if (endMs > virtualClipDurationInMs) endMs = virtualClipDurationInMs;

      const timedMdfs = services.getTimedMdfs();
      const timedMdf = timedMdfs.filter(mdf => mdf.id === oldTimedMdfItem.data.formId)[0] as MetaDataSchema;
      const errors = timedMdf.sanitize(services.getOptionLists(), oldTimedMdfItem.data, false);

      const newTimedMdfItem: TimedMetadataItemModel = {
        ...oldTimedMdfItem,
        startMs: currentTime,
        endMs,
        id: createUuid(),
        swimlane: 0,
        hasValidationErrors: !isEmpty(errors),
        isNewNonSavedItem: true,
        creationInfo: {
          source: 'api',
          date: new Date().toISOString(),
          userId: services.getCurrentUserId(),
        },
      };
      mutableState.collectionItems.value.push(newTimedMdfItem);
      if (newTimedMdfItem.isNewNonSavedItem) {
        mutableState.eventsSnapshot.value.push(newTimedMdfItem);
      }
      setItemHasChanged(newTimedMdfItem.id, true);
      services.clearTimelineState();
      setSelectedItemInternal({
        playerOps,
        item: newTimedMdfItem,
        setStart: true,
        shouldAutoFocusSelectedItem: false,
      });
    },

    itemHasChanged(timedItemId: string, hasChanged: boolean): void {
      setItemHasChanged(timedItemId, hasChanged);
    },

    updateItemsWithSnapshot(): void {
      const itemsWithChanges = mutableState.itemsWithChanges.value;
      const snapshot = mutableState.eventsSnapshot.value;
      const items = mutableState.collectionItems.value;

      const selectedItem = mutableState.selectedItem.value;
      if (selectedItem?.isNewNonSavedItem) {
        ops.remove(selectedItem);
      }

      itemsWithChanges.forEach(id => {
        const snapshotItem = snapshot.find(item => item.id === id);
        if (snapshotItem) {
          const itemToUpdate = items.find(item => item.id === id);
          if (itemToUpdate) {
            Object.assign(itemToUpdate, snapshotItem);
          }
        }
      });
      mutableState.selectedItem.value = null;
      mutableState.itemsWithChanges.value = [];
    },

    async saveItems(payload: SaveTimedMdfItemsPayload): Promise<void> {
      const { timedItemIds, abortController } = payload;
      mutableState.timedMdfSaving.value = true;
      try {
        const itemsById = keyBy(mutableState.collectionItems.value, it => it.id);
        const update: UpdateTimedMetadataRequestDto['items'] = {};
        for (const itemId of timedItemIds) {
          const item = itemsById[itemId];
          if (item?.hasValidationErrors) {
            continue;
          }
          update[itemId] = item ? modelToDto(item) : null;
        }
        if (isEmpty(update)) {
          return;
        }
        const request: UpdateTimedMetadataRequestDto = {
          items: update,
        };
        const itemId = services.getCurrentItem()?.id;
        if (!itemId) throw new Error('Expected current item to be present');

        await abortable(
          async () => {
            await updateTimedMetadata(itemId, request);
          },
          abortController,
        );

        const mdfs = services.getTimedMdfs();
        const usageRules = getRestrictedFormUsageRules(mdfs);
        const currentItemForIngestTime = services.getCurrentItem();
        timedItemIds.forEach(timedItemId => {
          setItemHasChanged(timedItemId, false);
          const item = itemsById[timedItemId];
          if (item) {
            item.isNewNonSavedItem = false;
            if (usageRules && currentItemForIngestTime) {
              Vue.set(item, 'usageRule', getUsageRuleFromTimedMdf(item, usageRules));
              applyIngestTimeIfUsageRuleExists(item, getIngestTimeFromItem(currentItemForIngestTime));
            }
          }
        });
        mutableState.eventsSnapshot.value = cloneDeep(mutableState.collectionItems.value);
        showToast('Log events updated', TOAST.SUCCESS);
      } catch (err) {
        if (isAbortError(err)) throw err;

        if ((err as AxiosError).response?.status === 400) logErrorAndShowToast('Please fix all validation errors in all log events', err, 'ex in saveItems');
        else logErrorAndShowToast('Failed updating log events, please try again later', err, 'ex in saveItems');
      } finally {
        mutableState.timedMdfSaving.value = false;
      }
    },

    setSelectedTimedMdf(mdf: MetaDataSchema | null): void {
      mutableState.selectedTimedMdf.value = mdf;
    },

    setLoadedCollection(loaded: boolean): void {
      mutableState.loadedCollection.value = loaded;
    },
  };

  return {
    get state() { return mutableState; },
    ops,
  };
}
